import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

const MAX_LEN = 100;

/**
 * Small icon button that opens a popover to send a quick message.
 * - judgeNumber=null  => "message all judges"
 * - judgeNumber=N     => "message judge N"
 */
export default function ManagerMessageButton({ judgeNumber = null, judgeLabel = null, onSend, size = "sm" }) {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);

  const isAll = !judgeNumber;
  const triggerLabel = isAll ? t("comms.messageAll") : t("comms.messageJudge");

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const value = text.trim().slice(0, MAX_LEN);
    if (!value) {
      toast.error(t("comms.emptyMessage"));
      return;
    }
    setSending(true);
    try {
      await onSend(value, judgeNumber);
      toast.success(isAll ? t("comms.sentAll") : t("comms.sentJudge").replace("{n}", judgeNumber));
      setText("");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={isAll ? "default" : "outline"}
          size={size}
          className="gap-1"
          data-testid={isAll ? "message-all-judges-btn" : `message-judge-${judgeNumber}-btn`}
          title={triggerLabel}
        >
          <MessageSquare size={14} />
          {isAll && <span className="hidden sm:inline">{t("comms.messageAllShort")}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <form onSubmit={onSubmit}>
          <div className="text-sm font-bold text-foreground mb-1">
            {isAll
              ? t("comms.composerTitleAll")
              : t("comms.composerTitleJudge").replace("{n}", judgeLabel || judgeNumber)}
          </div>
          <div className="text-xs text-muted-foreground mb-2">
            {t("comms.composerHint")}
          </div>
          <Input
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
            placeholder={t("comms.composerPlaceholder")}
            maxLength={MAX_LEN}
            data-testid="manager-composer-input"
          />
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-muted-foreground">{text.length}/{MAX_LEN}</span>
            <Button
              type="submit"
              size="sm"
              disabled={sending || !text.trim()}
              data-testid="manager-composer-send"
            >
              <Send size={14} className="mr-1" /> {t("comms.send")}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
